import React from 'react';
import { Server, Globe, Shield, Mail, Cloud, Database, AlertTriangle, ExternalLink, Printer, ChevronRight, Cpu } from 'lucide-react';

const WIDA_DISCLAIMER = "WIDA\u00AE is a registered trademark of the Board of Regents of the University of Wisconsin System. MOSAIC is not affiliated with, endorsed by, or sponsored by WIDA.";

const ServiceCard = ({ icon: Icon, name, tag, tagColor, description, needs, warning, info }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4 shadow-sm">
    <div className="flex items-center gap-3 mb-3">
      <Icon size={20} className="text-blue-600" />
      <h3 className="text-gray-900 font-bold text-lg">{name}</h3>
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tagColor}`}>{tag}</span>
    </div>
    <p className="text-gray-600 text-sm mb-3">{description}</p>
    <ul className="space-y-1 mb-3">
      {needs.map((n, i) => (
        <li key={i} className="text-gray-500 text-sm flex items-start gap-2">
          <ChevronRight size={12} className="mt-1 text-blue-500 flex-shrink-0" />
          <span>{n}</span>
        </li>
      ))}
    </ul>
    {warning && (
      <div className="bg-amber-50 border-l-2 border-amber-500 px-3 py-2 rounded text-amber-700 text-xs mt-2">
        <strong>Warning:</strong> {warning}
      </div>
    )}
    {info && (
      <div className="bg-blue-50 border-l-2 border-blue-500 px-3 py-2 rounded text-blue-700 text-xs mt-2">
        <strong>Note:</strong> {info}
      </div>
    )}
  </div>
);

const CredentialBlock = ({ title, icon: Icon, url, fields }) => (
  <div className="bg-white border-2 border-blue-200 rounded-lg p-5 mb-4 shadow-sm print:break-inside-avoid">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={18} className="text-blue-600" />
      <h3 className="text-gray-900 font-bold">{title}</h3>
    </div>
    {url && <p className="text-gray-400 text-xs mb-3">URL: {url}</p>}
    <div className="space-y-2">
      {fields.map((f, i) => (
        <div key={i} className="flex items-center border-b border-dotted border-gray-300 pb-1">
          <span className="text-gray-600 text-sm font-medium w-40 flex-shrink-0">{f.label}:</span>
          {f.value ? (
            <span className="text-gray-800 text-sm">{f.value}</span>
          ) : (
            <span className="flex-1 border-b border-gray-300 min-h-[18px] text-gray-400 text-xs italic">write here</span>
          )}
        </div>
      ))}
    </div>
  </div>
);

const SetupStep = ({ number, title, items }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3 mb-2">
      <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">{number}</span>
      <h3 className="text-gray-900 font-bold">{title}</h3>
    </div>
    <div className="ml-10 space-y-1">
      {items.map((item, i) => (
        <label key={i} className="flex items-start gap-2 text-gray-600 text-sm cursor-pointer">
          <input type="checkbox" className="mt-1 accent-blue-600" />
          <span>{item}</span>
        </label>
      ))}
    </div>
  </div>
);

const InfrastructureGuideTab = () => {
  const handlePrint = () => {
    window.open('/mosaic-infrastructure-guide.html', '_blank');
  };

  return (
    <div className="space-y-6" data-testid="infrastructure-guide-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Infrastructure & Service Guide</h2>
          <p className="text-gray-500 text-sm">Everything that powers MOSAIC — what each service does, how they connect, and credentials.</p>
        </div>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          data-testid="print-infra-guide-btn"
        >
          <Printer size={14} />
          Print for Binder
        </button>
      </div>

      {/* === SECTION 1: Services === */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">1. Your Services — What Each One Does</h2>

        <ServiceCard
          icon={Mail}
          name="Google Workspace"
          tag="EMAIL & DOCS"
          tagColor="bg-green-100 text-green-700"
          description="Your business email (info@, orders@, support@mosaicassessmentco.com), Google Drive, Docs, Sheets, and Calendar. This is your company's communication hub."
          needs={[
            "Professional email addresses for sales, support, and order confirmations",
            "Where schools and districts email you",
            "Document storage for contracts, purchase orders, invoices",
            "Team calendars for demos, meetings, school visits",
          ]}
          info="If this goes down, you can't send/receive email. MOSAIC the platform still works — this is separate from the app."
        />

        <ServiceCard
          icon={Globe}
          name="Namecheap"
          tag="DOMAIN REGISTRAR"
          tagColor="bg-orange-100 text-orange-700"
          description="You own the domain name mosaicassessmentco.com through Namecheap. Think of Namecheap as the title deed office — they prove you own this address on the internet."
          needs={[
            "Without a domain, there's no mosaicassessmentco.com",
            "You renew this annually (~$12-15/year)",
            "You only log in here to renew or to change where the domain points (nameservers)",
          ]}
          warning="If your domain expires, your entire website goes offline. Set it to auto-renew in Namecheap."
        />

        <ServiceCard
          icon={Shield}
          name="Cloudflare"
          tag="DNS & SECURITY"
          tagColor="bg-yellow-100 text-yellow-700"
          description="Cloudflare is the traffic controller. When someone types mosaicassessmentco.com, Cloudflare tells their browser where to find your app. It also protects your site from attacks and makes it load faster."
          needs={[
            "Routes mosaicassessmentco.com to your Emergent production server",
            "Free SSL certificate (the padlock icon — schools require this)",
            "DDoS protection (blocks malicious traffic)",
            "Speed boost via CDN for schools across NYC",
          ]}
          info="If this goes down: extremely rare. If DNS settings get changed accidentally, your domain stops pointing to your app. Fix in Cloudflare dashboard, DNS tab."
        />

        <ServiceCard
          icon={Server}
          name="Emergent"
          tag="APP HOSTING & DATABASE"
          tagColor="bg-blue-100 text-blue-700"
          description="Emergent hosts the actual MOSAIC application — the code, the database (MongoDB), the server that runs everything. Think of it as the building where your app lives."
          needs={[
            "Runs the MOSAIC web app (React frontend + FastAPI backend)",
            "Stores all data: student accounts, exam results, questions, quotes, leads",
            "Handles Automated rubric support (via Emergent Universal content-engine Key)",
            "Generates PDFs (quotes, print booklets, procurement letters)",
          ]}
          warning="Cost: 50 credits/month for production deployment. content-engine Key: Automated rubric support uses Universal Key balance. Top up via Profile, Universal Key, Add Balance."
        />

        <ServiceCard
          icon={Mail}
          name="SendGrid"
          tag="TRANSACTIONAL EMAIL"
          tagColor="bg-purple-100 text-purple-700"
          description='Sends automated emails from MOSAIC — welcome emails to new schools, lead nurture drip sequences, password resets. This is not your personal email — it is the robot that sends emails on MOSAIC behalf.'
          needs={[
            '"Welcome to MOSAIC" onboarding emails with login credentials',
            "Automated lead drip email sequences",
            "Transactional emails (password reset, notifications)",
          ]}
          warning="You must verify your sender domain (noreply@mosaicassessmentco.com) in SendGrid Dashboard, Settings, Sender Authentication. Without this, emails get a 403 Forbidden error."
        />
      </div>

      {/* === SECTION 2: How They Connect === */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">2. How They All Connect</h2>

        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4 shadow-sm">
          <p className="text-gray-500 text-sm mb-4 font-medium">When a teacher visits mosaicassessmentco.com:</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <span className="bg-amber-50 text-amber-700 px-4 py-2 rounded-lg text-sm font-bold border border-amber-200">Teacher&apos;s Browser</span>
            <span className="text-gray-400 text-lg">&rarr;</span>
            <span className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm font-bold border border-blue-200">Namecheap &quot;I own this domain&quot;</span>
            <span className="text-gray-400 text-lg">&rarr;</span>
            <span className="bg-yellow-50 text-yellow-700 px-4 py-2 rounded-lg text-sm font-bold border border-yellow-200">Cloudflare &quot;Here&apos;s where it lives&quot;</span>
            <span className="text-gray-400 text-lg">&rarr;</span>
            <span className="bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-bold border border-green-200">Emergent &quot;Here&apos;s the app!&quot;</span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4 shadow-sm">
          <p className="text-gray-500 text-sm mb-4 font-medium">When MOSAIC sends an automated email:</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <span className="bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-bold border border-green-200">Emergent (MOSAIC app)</span>
            <span className="text-gray-400 text-lg">&rarr;</span>
            <span className="bg-purple-50 text-purple-700 px-4 py-2 rounded-lg text-sm font-bold border border-purple-200">SendGrid &quot;I&apos;ll deliver it&quot;</span>
            <span className="text-gray-400 text-lg">&rarr;</span>
            <span className="bg-amber-50 text-amber-700 px-4 py-2 rounded-lg text-sm font-bold border border-amber-200">School&apos;s Inbox</span>
          </div>
        </div>

        {/* Dependency table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-600">
                <th className="text-left p-3 text-white font-medium">If THIS breaks...</th>
                <th className="text-left p-3 text-white font-medium">THIS happens...</th>
                <th className="text-left p-3 text-white font-medium">Fix it HERE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ["Namecheap (domain expires)", "Entire site goes offline", "namecheap.com \u2014 renew domain"],
                ["Cloudflare (DNS misconfigured)", "Domain doesn't reach your app", "cloudflare.com \u2192 DNS tab"],
                ["Emergent (server down)", "App doesn't load, database unavailable", "Emergent dashboard or support"],
                ["SendGrid (key invalid)", "Automated emails stop sending", "sendgrid.com \u2192 API Keys"],
                ["Google Workspace", "Can't send/receive business email", "admin.google.com"],
                ["Emergent content-engine Key (no balance)", "Automated rubric support stops working", "Emergent \u2192 Profile \u2192 Universal Key"],
              ].map(([cause, effect, fix], i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="p-3 text-red-600 font-medium">{cause}</td>
                  <td className="p-3 text-gray-700">{effect}</td>
                  <td className="p-3 text-blue-600">{fix}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* === SECTION 3: Setup Steps === */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">3. Step-by-Step Setup Guide</h2>
        <p className="text-gray-500 text-sm mb-4">Follow these steps in order. Check each box as you complete it.</p>

        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <SetupStep number="1" title="Create Cloudflare Account" items={[
            "Go to cloudflare.com \u2192 Sign Up",
            "Enter email and password \u2192 Click Sign Up",
            "Verify your email (check inbox)",
          ]} />
          <SetupStep number="2" title="Add Your Domain to Cloudflare" items={[
            'Click "+ Add a site"',
            "Type: mosaicassessmentco.com",
            "Select the Free plan \u2192 Continue",
            "Let Cloudflare scan DNS records \u2192 Continue",
            "Write down the 2 nameservers Cloudflare gives you",
          ]} />
          <SetupStep number="3" title="Point Namecheap to Cloudflare" items={[
            "Log in to namecheap.com",
            'Domain List \u2192 Manage \u2192 Nameservers section',
            'Change to "Custom DNS"',
            "Paste your 2 Cloudflare nameservers",
            "Click the green checkmark to save",
          ]} />
          <SetupStep number="4" title="Wait & Verify (15 min to 24 hours)" items={[
            'In Cloudflare, click "Done, check nameservers"',
            "Wait for the green success message",
            "You'll get an email from Cloudflare when it's active",
          ]} />
          <SetupStep number="5" title="Deploy MOSAIC on Emergent" items={[
            'In the Emergent chat, click the "Deploy" button',
            "Follow the deployment prompts",
            "Enter your custom domain: mosaicassessmentco.com",
            "Follow the Entri guided DNS linking steps",
            'If asked for CNAME: Cloudflare \u2192 DNS \u2192 Add Record (Type: CNAME, Proxy: OFF / gray cloud)',
          ]} />
          <SetupStep number="6" title="Verify You're Live" items={[
            "Open browser \u2192 go to mosaicassessmentco.com",
            "You should see MOSAIC with Moi the mascot",
            "Log in with your Super Admin credentials",
            "Check for the padlock (SSL) icon in the address bar",
          ]} />
        </div>
      </div>

      {/* === SECTION 4: Credentials === */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1 border-b border-gray-200 pb-2">4. Credentials & Login Info</h2>
        <p className="text-red-500 text-xs font-bold mb-3">KEEP THIS SECTION SECURE. Use the printable version for your binder.</p>

        <CredentialBlock title="Google Workspace" icon={Mail} url="admin.google.com" fields={[
          { label: "Admin Email" }, { label: "Password" }, { label: "Recovery Email" }, { label: "Monthly Cost" }, { label: "Renewal Date" },
        ]} />
        <CredentialBlock title="Namecheap" icon={Globe} url="namecheap.com" fields={[
          { label: "Username" }, { label: "Password" }, { label: "Domain", value: "mosaicassessmentco.com" }, { label: "Expiration Date" }, { label: "Auto-Renew On?" },
        ]} />
        <CredentialBlock title="Cloudflare" icon={Shield} url="dash.cloudflare.com" fields={[
          { label: "Email" }, { label: "Password" }, { label: "Nameserver 1" }, { label: "Nameserver 2" }, { label: "Plan", value: "Free" },
        ]} />
        <CredentialBlock title="Emergent" icon={Server} url="emergentagent.com" fields={[
          { label: "Email" }, { label: "Password" }, { label: "Production URL" }, { label: "Monthly Cost", value: "50 credits/month" }, { label: "content-engine Key Balance" },
        ]} />
        <CredentialBlock title="SendGrid" icon={Mail} url="app.sendgrid.com" fields={[
          { label: "Email" }, { label: "Password" }, { label: "API Key Name" }, { label: "Sender Verified?" }, { label: "Plan", value: "Free (100 emails/day)" },
        ]} />
        <CredentialBlock title="MOSAIC Super Admin" icon={Database} url="mosaicassessmentco.com/login" fields={[
          { label: "Email" }, { label: "Password" }, { label: "Role", value: "Super Admin" },
        ]} />
      </div>

      {/* === SECTION 5: Costs === */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">5. Monthly Costs Summary</h2>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-600">
                <th className="text-left p-3 text-white font-medium">Service</th>
                <th className="text-left p-3 text-white font-medium">Cost</th>
                <th className="text-left p-3 text-white font-medium">Billing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ["Google Workspace", "$7.20/user/month", "Monthly"],
                ["Namecheap (domain)", "~$12-15/year", "Annual"],
                ["Cloudflare", "Free", "\u2014"],
                ["Emergent (hosting)", "50 credits/month", "Monthly"],
                ["Emergent (content-engine Key)", "Pay as you go", "Per usage"],
                ["SendGrid", "Free (100 emails/day)", "\u2014"],
              ].map(([svc, cost, billing], i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="p-3 text-gray-800 font-medium">{svc}</td>
                  <td className="p-3 text-green-600">{cost}</td>
                  <td className="p-3 text-gray-500">{billing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* === SECTION 6: Support === */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">6. Support Contacts</h2>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-600">
                <th className="text-left p-3 text-white font-medium">Service</th>
                <th className="text-left p-3 text-white font-medium">Support</th>
                <th className="text-left p-3 text-white font-medium">Response</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ["Namecheap", "namecheap.com/support \u2192 Live Chat", "~5 min (24/7)"],
                ["Cloudflare", "dash.cloudflare.com \u2192 Support", "Community (free)"],
                ["Emergent", "In-app chat or support page", "Same day"],
                ["SendGrid", "support.sendgrid.com", "24-48 hours (free)"],
                ["Google Workspace", "admin.google.com \u2192 Support", "Phone/chat"],
              ].map(([svc, url, time], i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="p-3 text-gray-800 font-medium">{svc}</td>
                  <td className="p-3 text-blue-600">{url}</td>
                  <td className="p-3 text-gray-500">{time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-gray-400 text-[10px] text-center mt-6">{WIDA_DISCLAIMER}</p>
    </div>
  );
};

export default InfrastructureGuideTab;
